import moment from 'moment';
import { criarOfertasIniciais, criarViagens } from './dados';

const ATRASO_SIMULADO_MS = 350;
const TAMANHO_PADRAO_DA_PAGINA = 15;
const BASE = '/painel-oferta';

const ofertas = criarOfertasIniciais();
const viagens = criarViagens(ofertas);

const comoLista = (valor) => (Array.isArray(valor) ? valor : valor === undefined || valor === null || valor === '' ? [] : [valor]);

const normalizar = (texto) => String(texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const textoDoCliente = (empresa) => normalizar([empresa?.emp_nome_fantasia, empresa?.emp_razao_social, empresa?.emp_cnpj].join(' '));

const paginar = (itens, parametros) => {
    const porPagina = Number(parametros.per_page) || TAMANHO_PADRAO_DA_PAGINA;
    const pagina = Math.max(1, Number(parametros.page) || 1);

    return {
        data: itens.slice((pagina - 1) * porPagina, pagina * porPagina),
        pagination: { total: itens.length, current_page: pagina, per_page: porPagina, last_page: Math.max(1, Math.ceil(itens.length / porPagina)) },
    };
};

const contarPor = (itens, campo) => itens.reduce((contagem, item) => ({ ...contagem, [item[campo]]: (contagem[item[campo]] || 0) + 1 }), {});

const contarComVolumePor = (itens, campo) => itens.reduce((contagem, item) => ({
    ...contagem,
    [item[campo]]: {
        count: (contagem[item[campo]]?.count || 0) + 1,
        volume: (contagem[item[campo]]?.volume || 0) + Number(item.volume || 0),
    },
}), {});

const textoDaOferta = (oferta) => normalizar([
    oferta.numero, oferta.produto_acondicionamento?.produto_nome, oferta.numero_documento, oferta.numero_pedido,
].join(' '));

const listarOfertas = (parametros) => {
    const termo = normalizar(parametros.termo);
    const cliente = normalizar(parametros.cliente);
    const status = comoLista(parametros.status);

    const doFiltroBase = ofertas
        .filter((oferta) => !termo || textoDaOferta(oferta).includes(termo))
        .filter((oferta) => !cliente || textoDoCliente(oferta.embarcadora).includes(cliente))
        .filter((oferta) => !parametros.data_inicio || moment(oferta.data_fim_operacao).isSameOrAfter(parametros.data_inicio, 'day'))
        .filter((oferta) => !parametros.data_fim || moment(oferta.data_inicio_operacao).isSameOrBefore(parametros.data_fim, 'day'));

    const filtradas = doFiltroBase.filter((oferta) => status.length === 0 || status.includes(oferta.status));
    const { data, pagination } = paginar([...filtradas].sort((a, b) => b.id - a.id), parametros);

    return { data, meta: { pagination, counts: contarPor(doFiltroBase, 'status') } };
};

const listarViagens = (parametros) => {
    const termo = normalizar(parametros.termo);
    const cliente = normalizar(parametros.cliente);
    const status = comoLista(parametros.status);

    const doFiltroBase = viagens
        .filter((viagem) => !parametros.oferta_id || Number(viagem.oferta_id) === Number(parametros.oferta_id))
        .filter((viagem) => !cliente || textoDoCliente(viagem.oferta?.embarcadora).includes(cliente))
        .filter((viagem) => !termo || normalizar([viagem.numero, viagem.motorista.nome, viagem.cavalo.placa, viagem.carroceria.placa].join(' ')).includes(termo));

    const filtradas = doFiltroBase.filter((viagem) => status.length === 0 || status.includes(viagem.status));
    const { data, pagination } = paginar(filtradas, parametros);

    return { data, meta: { pagination, counts: contarComVolumePor(doFiltroBase, 'status') } };
};

const falhar = (status, mensagem) => ({ status, data: { message: mensagem, errors: [mensagem] } });

const FINALIZADO = 'finalizado';

const locaisDaOferta = (oferta) => [
    ['ARMAZEM', oferta?.armazem_origem, 'ARMAZEM_ORIGEM'],
    ['ARMAZEM', oferta?.armazem_destino, 'ARMAZEM_DESTINO'],
].filter(([, empresa]) => empresa).map(([tipo, empresa, papel]) => ({
    tipo,
    id: empresa.id,
    nome: empresa.emp_nome_fantasia,
    latitude: Number(empresa.emp_latitude),
    longitude: Number(empresa.emp_longitude),
    localizacao_exata: true,
    papeis: [papel],
}));

const indicadoresDe = (ativas) => ({
    em_operacao: ativas.length,
    a_caminho: ativas.filter((viagem) => viagem.status === 'a_caminho_carregamento').length,
    carregando: ativas.filter((viagem) => ['patio_origem', 'aguardando_carregamento'].includes(viagem.status)).length,
    em_transito: ativas.filter((viagem) => viagem.status === 'em_transito').length,
    atrasados: ativas.filter((viagem) => viagem.situacao === 'atrasado').length,
    sem_sinal: ativas.filter((viagem) => viagem.situacao === 'sem_sinal').length,
    ocorrencias: ativas.filter((viagem) => viagem.ocorrencia_ativa).length,
});

const monitorar = (parametros) => {
    const oferta = ofertas.find((item) => Number(item.id) === Number(parametros.oferta_id));
    const ativas = viagens.filter((viagem) => Number(viagem.oferta_id) === Number(parametros.oferta_id) && viagem.status !== FINALIZADO);
    const { data, pagination } = paginar(ativas, parametros);

    return { data, meta: { pagination, indicadores: indicadoresDe(ativas), locais: locaisDaOferta(oferta) } };
};

const resolver = (metodo, caminho, parametros) => {
    if (metodo === 'get' && caminho === '') return { status: 200, data: listarOfertas(parametros) };
    if (metodo === 'get' && caminho === '/viagens') return { status: 200, data: listarViagens(parametros) };
    if (metodo === 'get' && caminho === '/monitoramento') return { status: 200, data: monitorar(parametros) };

    if (metodo === 'get' && /^\/viagens\/\d+$/.test(caminho)) {
        const viagem = viagens.find((item) => Number(item.id) === Number(caminho.split('/')[2]));
        return viagem ? { status: 200, data: { data: viagem } } : falhar(404, 'Viagem não encontrada.');
    }

    if (metodo === 'get' && /^\/\d+$/.test(caminho)) {
        const oferta = ofertas.find((item) => Number(item.id) === Number(caminho.slice(1)));
        return oferta ? { status: 200, data: { data: oferta } } : falhar(404, 'Oferta não encontrada.');
    }

    return falhar(404, 'Rota do painel de ofertas ainda não simulada.');
};

export const caminhoDoPainelDeOfertas = (url) => {
    const caminho = String(url).split('?')[0];
    const posicao = caminho.indexOf(BASE);
    return posicao === -1 ? null : caminho.slice(posicao + BASE.length).replace(/\/$/, '');
};

export const responderAoPainelDeOfertas = (configuracao) => {
    const resposta = resolver(String(configuracao.method || 'get').toLowerCase(), caminhoDoPainelDeOfertas(configuracao.url), configuracao.params || {});

    return new Promise((resolve, reject) => setTimeout(() => {
        const completa = { ...resposta, statusText: resposta.status < 400 ? 'OK' : 'Not Found', headers: {}, config: configuracao, request: {} };
        if (resposta.status >= 400) {
            const erro = new Error(resposta.data.message);
            erro.isAxiosError = true;
            erro.response = completa;
            erro.config = configuracao;
            reject(erro);
            return;
        }
        resolve(completa);
    }, ATRASO_SIMULADO_MS));
};
